import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, ArrowRight, ShoppingCart, Code, Truck, User, Bike, Factory, Heart, DollarSign,
  Check, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { LogoFull } from '@/components/Logo';

const ICON_MAP = {
  'shopping-cart': ShoppingCart,
  'code': Code,
  'truck': Truck,
  'user': User,
  'bike': Bike,
  'factory': Factory,
  'heart-pulse': Heart,
  'dollar-sign': DollarSign
};

const Templates = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const data = await api.getTemplates();
        setTemplates(data);
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const handleApplyTemplate = async (templateId) => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    setApplying(templateId);
    try {
      const policy = await api.applyTemplate(templateId);
      navigate('/builder', { state: { policyId: policy.policy_id } });
    } catch (error) {
      console.error('Error applying template:', error);
    } finally {
      setApplying(null);
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return 'bg-emerald-100 text-emerald-800';
      case 'low-medium': return 'bg-emerald-100 text-emerald-800';
      case 'medium': return 'bg-amber-100 text-amber-800';
      case 'medium-high': return 'bg-orange-100 text-orange-800';
      case 'high': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#DCE4ED]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <LogoFull size="small" />
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/builder">
                <Button variant="outline" className="border-[#DCE4ED]">
                  Skip to Builder
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 px-6 lg:px-12 bg-[#1A3A64] text-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm font-medium mb-6">
            <Sparkles size={16} />
            AI-Powered Templates
          </div>
          <h1 className="text-3xl lg:text-4xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Start with an Industry Template
          </h1>
          <p className="text-white/70 max-w-2xl mx-auto">
            Choose a pre-configured template based on your industry. Our AI has already selected the most 
            relevant coverages for your business type. You can customize everything later.
          </p>
        </div>
      </section>

      {/* Templates Grid */}
      <section className="py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {templates.map((template, index) => {
              const IconComponent = ICON_MAP[template.icon] || Shield;
              
              return (
                <motion.div
                  key={template.template_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl border border-[#DCE4ED] p-6 hover:border-[#1A3A64] hover:shadow-lg transition-all group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-[#1A3A64]/10 rounded-xl flex items-center justify-center">
                      <IconComponent size={24} className="text-[#1A3A64]" />
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(template.risk_profile)}`}>
                      {template.risk_profile} risk
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-[#0D1B2A] mb-2">{template.name}</h3>
                  <p className="text-sm text-[#3D5A80] mb-4">{template.description}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#8DA0B9]">Employees</span>
                      <span className="text-[#0D1B2A]">{template.typical_employee_range}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#8DA0B9]">Revenue</span>
                      <span className="text-[#0D1B2A]">{template.typical_revenue_range}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-[#DCE4ED] mb-4">
                    <p className="text-xs text-[#8DA0B9] uppercase tracking-wider mb-2">Included Coverages</p>
                    <div className="flex flex-wrap gap-1">
                      {template.recommended_coverages.slice(0, 3).map((coverage, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-[#F0F4F8] rounded text-xs text-[#3D5A80]">
                          <Check size={10} className="text-[#1A3A64]" />
                          {coverage.replace('_', ' ')}
                        </span>
                      ))}
                      {template.recommended_coverages.length > 3 && (
                        <span className="px-2 py-1 bg-[#F0F4F8] rounded text-xs text-[#8DA0B9]">
                          +{template.recommended_coverages.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>

                  <Button 
                    className="w-full btn-primary group-hover:bg-[#122B4D]"
                    onClick={() => handleApplyTemplate(template.template_id)}
                    disabled={applying === template.template_id}
                    data-testid={`apply-template-${template.template_id}`}
                  >
                    {applying === template.template_id ? 'Creating...' : 'Use This Template'}
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-6 lg:px-12 border-t border-[#DCE4ED]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-semibold text-[#0D1B2A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Don't see your industry?
          </h2>
          <p className="text-[#3D5A80] mb-6">
            No problem! Start from scratch and our AI will help you build the perfect custom policy.
          </p>
          <Button 
            variant="outline" 
            size="lg" 
            className="border-[#DCE4ED]"
            onClick={() => navigate(isAuthenticated ? '/builder' : '/auth')}
          >
            Start From Scratch
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Templates;
